import { useState } from 'react';
import { User, Mail, Phone, MapPin, Bell, Lock, Save, Megaphone } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { profile as profileApi } from '../lib/api';

export default function Profile() {
  const { user, refresh } = useAuth();
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [mobile, setMobile] = useState(user?.mobile || '');
  const [address, setAddress] = useState(user?.address || '');
  const [notifPref, setNotifPref] = useState(user?.notification_preference || 'email');
  const [marketingConsent, setMarketingConsent] = useState(!!user?.marketing_consent_at);
  const [profileMsg, setProfileMsg] = useState('');
  const [profileErr, setProfileErr] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwMsg, setPwMsg] = useState('');
  const [pwErr, setPwErr] = useState('');
  const [pwLoading, setPwLoading] = useState(false);

  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileMsg('');
    setProfileErr('');
    setProfileLoading(true);
    try {
      await profileApi.update({ full_name: fullName, mobile, address, notification_preference: notifPref, marketingConsent });
      await refresh();
      setProfileMsg('Profile updated successfully!');
    } catch (err: any) {
      setProfileErr(err.message || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwMsg('');
    setPwErr('');
    if (newPassword.length < 8) { setPwErr('New password must be at least 8 characters'); return; }
    if (newPassword !== confirmPassword) { setPwErr('Passwords do not match'); return; }
    setPwLoading(true);
    try {
      await profileApi.changePassword(currentPassword, newPassword);
      setPwMsg('Password updated successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setPwErr(err.message || 'Failed to update password');
    } finally {
      setPwLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <h1 className="text-2xl font-bold text-gray-900">Profile & Settings</h1>

      {/* Profile Info */}
      <div className="card space-y-5">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <User className="w-5 h-5 text-blue-600" /> Personal Information
        </h2>

        {profileMsg && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">{profileMsg}</div>}
        {profileErr && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{profileErr}</div>}

        <form onSubmit={handleProfileSave} className="space-y-4">
          <div>
            <label className="label flex items-center gap-1">
              <Mail className="w-3.5 h-3.5" /> Email address
            </label>
            <input
              type="email"
              className="input-field bg-gray-50 text-gray-500 cursor-not-allowed"
              value={user?.email || ''}
              readOnly
            />
            <p className="text-xs text-gray-400 mt-1">Email cannot be changed</p>
          </div>
          <div>
            <label className="label flex items-center gap-1">
              <User className="w-3.5 h-3.5" /> Full name
            </label>
            <input
              type="text"
              className="input-field"
              placeholder="Jane Smith"
              value={fullName}
              onChange={e => setFullName(e.target.value)}
            />
          </div>
          <div>
            <label className="label flex items-center gap-1">
              <Phone className="w-3.5 h-3.5" /> Mobile number
            </label>
            <input
              type="tel"
              className="input-field"
              placeholder="+1 555 123 4567"
              value={mobile}
              onChange={e => setMobile(e.target.value)}
            />
          </div>
          <div>
            <label className="label flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" /> Address
            </label>
            <textarea
              className="input-field resize-none"
              placeholder="Your address"
              value={address}
              onChange={e => setAddress(e.target.value)}
              rows={2}
            />
          </div>
          <div>
            <label className="label flex items-center gap-1">
              <Bell className="w-3.5 h-3.5" /> Reminder notification
            </label>
            <select
              className="input-field"
              value={notifPref}
              onChange={e => setNotifPref(e.target.value)}
            >
              <option value="email">Email (default)</option>
              <option value="none">No notifications</option>
            </select>
          </div>
          <label className="flex items-start gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={marketingConsent}
              onChange={e => setMarketingConsent(e.target.checked)}
            />
            <span className="flex items-center gap-1.5">
              <Megaphone className="w-3.5 h-3.5 text-gray-400" />
              I'd like to receive occasional tips and product updates by email
            </span>
          </label>
          <button type="submit" className="btn-primary flex items-center gap-2" disabled={profileLoading}>
            <Save className="w-4 h-4" />
            {profileLoading ? 'Saving...' : 'Save Profile'}
          </button>
        </form>
      </div>

      {/* Change Password */}
      <div className="card space-y-5">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Lock className="w-5 h-5 text-blue-600" /> Change Password
        </h2>

        {pwMsg && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">{pwMsg}</div>}
        {pwErr && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{pwErr}</div>}

        <form onSubmit={handlePasswordSave} className="space-y-4">
          {/* Show current password field unless user is Google-only (no way to have a password) */}
          <div>
            <label className="label">Current password <span className="text-gray-400 font-normal">(leave blank if you signed up with Google)</span></label>
            <input
              type="password"
              className="input-field"
              placeholder="••••••••"
              value={currentPassword}
              onChange={e => setCurrentPassword(e.target.value)}
            />
          </div>
          <div>
            <label className="label">New password</label>
            <input
              type="password"
              className="input-field"
              placeholder="Min. 8 characters"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              required
              minLength={8}
            />
          </div>
          <div>
            <label className="label">Confirm new password</label>
            <input
              type="password"
              className="input-field"
              placeholder="Repeat new password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              required
            />
          </div>
          <button type="submit" className="btn-primary flex items-center gap-2" disabled={pwLoading}>
            <Lock className="w-4 h-4" />
            {pwLoading ? 'Updating...' : 'Update Password'}
          </button>
        </form>
      </div>

      {/* Account info */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Account Details</h2>
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between">
            <dt className="text-gray-500">Account type</dt>
            <dd className="font-medium text-gray-900">{user?.is_admin ? 'Administrator' : 'User'}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">Email verified</dt>
            <dd className={`font-medium ${user?.email_verified ? 'text-green-600' : 'text-amber-600'}`}>
              {user?.email_verified ? 'Verified' : 'Pending'}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">Sign-in method</dt>
            <dd className="font-medium text-gray-900">{user?.google_id ? 'Google' : 'Email & Password'}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">Member since</dt>
            <dd className="font-medium text-gray-900">
              {user?.created_at ? new Date(user.created_at * 1000).toLocaleDateString() : '—'}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
}
